import { useAuth } from 'contexts/auth';
import { useSnackbar } from 'contexts/snackbar';
import { createContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Icon, Stack } from 'shell/components';
import { getPasswordRequirements, isValidPassword } from 'utils/password';
import { emailRegex } from 'utils/regex';

const defaultForm = {
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
    code: '',
};

const defaultErrors = {
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
    code: '',
};

const PAGES = {
    CREATE_ACCOUNT: 'sign-up',
    PROFILE: 'profile',
    VERIFY_EMAIL: 'verify-email',
};

const PageContext = createContext();

const PageProvider = (props) => {
    const history = useHistory();
    const { register, resendConfirmationCode, verifyUser, signInWithGoogle } = useAuth();
    const { message } = useSnackbar();
    const { t } = useTranslation();

    const COUNTRY_OPTIONS = useMemo(
        () => [
            {
                key: 'canada',
                value: (
                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                        <Icon kind={'canada'} />
                        {t('country.canada', 'Canada')}
                    </Stack>
                ),
            },
            {
                key: 'usa',
                value: (
                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                        <Icon kind={'usa'} />
                        {t('country.usa', 'USA')}
                    </Stack>
                ),
            },
        ],
        [t]
    );

    const [loading, setLoading] = useState(false);
    const [country, setCountry] = useState(COUNTRY_OPTIONS[0].key);
    const [form, setForm] = useState(defaultForm);
    const [errors, setErrors] = useState(defaultErrors);
    const [currentStep, setCurrentStep] = useState(PAGES.CREATE_ACCOUNT);

    const handleSignUp = async () => {
        if (!validateAll(['email'])) return;
        setCurrentStep(PAGES.PROFILE);
    };

    const handleCreateAccount = async () => {
        try {
            if (!validateAll(['firstName', 'lastName', 'password', 'confirmPassword'])) return;
            setLoading(true);
            const signUp = await register({
                username: form.email,
                password: form.password,
                firstName: form.firstName,
                lastName: form.lastName,
            });
            if (signUp === 'UsernameExistsException') {
                setErrors((prev) => ({ ...prev, email: t('errors.email-in-use', 'This email is already in use') }));
                setCurrentStep(PAGES.CREATE_ACCOUNT);
            } else if (signUp === 'Unconfirmed') {
                setErrors((prev) => ({ ...prev, email: '' }));
                setCurrentStep(PAGES.VERIFY_EMAIL);
            } else if (signUp !== true) {
                setErrors((prev) => ({ ...prev, email: t('errors.sign-up-failed', 'Unable to create account') }));
                setCurrentStep(PAGES.CREATE_ACCOUNT);
            } else {
                setErrors((prev) => ({ ...prev, email: '' }));
                setCurrentStep(PAGES.VERIFY_EMAIL);
            }
            setLoading(false);
            return signUp;
        } catch (e) {
            console.log(e);
            setLoading(false);
            return false;
        }
    };

    const handleConfirmationCode = async () => {
        try {
            if (!form.code.trim()) {
                return setErrors((current) => ({ ...current, code: t('errors.valid-code', 'Enter a valid code') }));
            }
            setLoading(true);
            const verifyTheUser = await verifyUser(form.email, form.code.trim());
            if (verifyTheUser?.success) {
                message.success(
                    t('common:message.verification-success', 'Verification successful. Please log in again')
                );
                return history.push('/', { email: form.email });
            }
        } catch (e) {
            const error =
                e?.name === 'ExpiredCodeException'
                    ? t('errors.code-expired-resend', 'The code has expired; please resend the email')
                    : t('errors.valid-code', 'Enter a valid code');
            setErrors((current) => ({ ...current, code: error }));
        } finally {
            setLoading(false);
        }
    };

    const handleResendCode = async () => {
        return resendConfirmationCode(form.email);
    };

    const handleChange = async (key, callback) => {
        switch (key) {
            case 'email': {
                return setForm((prev) => ({ ...prev, email: callback }));
            }
            case 'firstName':
            case 'lastName':
            case 'password':
            case 'confirmPassword':
            case 'code':
                return setForm((prev) => ({ ...prev, [key]: callback }));
            case 'country': {
                const currentUrl = window.location.origin;
                setCountry(callback);
                if (callback === 'canada' && currentUrl !== 'https://app.ensightful.ca') {
                    window.location.replace('https://app.ensightful.ca/sign-up');
                } else if (callback === 'usa' && currentUrl !== 'https://app.ensightful.us') {
                    window.location.replace('https://app.ensightful.us/sign-up');
                }
                return;
            }
            default:
                return;
        }
    };

    const getErrorMessage = (key, value) => {
        value = value?.trim();
        switch (key) {
            case 'email':
                return value && emailRegex.test(value) ? '' : t('errors.invalid-email', 'Please enter a valid email.');
            case 'firstName':
                return value ? '' : t('errors.first-name-required', 'Please enter your first name');
            case 'lastName':
                return value ? '' : t('errors.last-name-required', 'Please enter your last name');
            case 'password':
                return isValidPassword(value)
                    ? ''
                    : t('errors.password-requirements', 'Please satisfy all requirements');
            case 'confirmPassword':
                return value === form.password ? '' : t('errors.passwords-mismatch', 'Passwords do not match');
            default:
                return '';
        }
    };

    const validate = (key, value) => {
        const errorMessage = getErrorMessage(key, value);
        setErrors((errors) => {
            return {
                ...errors,
                [key]: errorMessage,
            };
        });
    };

    const validateAll = (keys = Object.keys(errors)) => {
        return !Boolean(
            keys
                .map((key) => {
                    validate(key, form[key]);
                    return getErrorMessage(key, form[key]);
                })
                .reduce((cur, acc) => {
                    return cur + acc;
                }, '')
        );
    };

    useEffect(() => {
        if (form.email?.length > 0) {
            setForm((prev) => {
                return { ...prev, email: form.email };
            });
        }
    }, [form?.email]);

    useEffect(() => {
        const region = process.env.REACT_APP_AWS_REGION;
        if (region === 'ca-central-1') {
            setCountry(COUNTRY_OPTIONS[0].key);
        } else if (region === 'us-east-1') {
            setCountry(COUNTRY_OPTIONS[1].key);
        } else {
            setCountry(COUNTRY_OPTIONS[1].key);
        }
    }, [COUNTRY_OPTIONS]);

    return (
        <PageContext.Provider
            value={{
                loading,
                country,
                form,
                errors,
                currentStep,

                handleChange,
                handleConfirmationCode,
                handleSignUp,
                handleCreateAccount,
                handleResendCode,
                passwordRequirements: getPasswordRequirements(form.password),
                setErrors,
                setForm,
                setCurrentStep,
                signInWithGoogle,
                PAGES,
                COUNTRY_OPTIONS,
            }}
            {...props}
        />
    );
};

export { PageContext };
export default PageProvider;
